import { Button, Classes, Hotkey, Hotkeys, Icon, Intent, Popover, Position, Tooltip } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import autobind from "autobind-decorator";
import React from "react";
import ScrollArea from "react-scrollbar"
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { connect } from "react-redux";

import ILocalCacheService from "core/service/cache/ILocalCacheService";
import ServiceRegistry from "core/util/ServiceRegistry";
import initialState from "../../reducers/initialState";
import ActiveEdit from "./ActiveEdit";
import AdminPage from "./AdminPage";
import { ConfigFormType } from "./ConfigForm";
import EditControls from "./EditControls";
import EditMenu from "./EditMenu";
import { EditorConfig } from "./EditorConfigForm";
import { Handel } from "./styled/fragments";
import PopupWindow from "./styled/PopupWindow";
import SidebarContainer from "./styled/SidebarContainer";

interface IEditorWrapperState {
	barOpen: boolean;
	popupOpen: boolean;
	selected: {
		id?: string;
		instance?: string | null;
	} | null;
	selectedType: ConfigFormType;
	activePanels: string[];
	config: EditorConfig;
}

export interface IActivePanelActions {
	addPanel: (instance: string) => void;
	removePanel: (instance: string) => void;
}

export const PanelsActiveContext = React.createContext<IActivePanelActions>(undefined);


@DragDropContext(HTML5Backend)
class EditorWrapper extends React.Component<any, IEditorWrapperState> {

	constructor(props) {
		super(props);

		const config = ServiceRegistry.get(ILocalCacheService).getAs("config", EditorConfig, "editor");

		this.state = {
			barOpen: false,
			popupOpen: false,
			selected: null,
			selectedType: null,
			activePanels: [],
			config
		};
	}

	@autobind
	// @Debounce()
	private onConfigChange(config: EditorConfig) {
		const cache = ServiceRegistry.get(ILocalCacheService);
		cache.put("config", config, "editor");
		this.setState({config});
	}

	public renderHotkeys() {
		return (
			<Hotkeys>
				<Hotkey
						global={true}
						combo="ctrl + e"
						label="Be awesome all the time"
						onKeyDown={() => console.log("Awesome!")}
				/>
				<Hotkey
						group="Fancy shortcuts"
						combo="shift + f"
						label="Be fancy only when focused"
						onKeyDown={() => console.log("So fancy!")}
				/>
			</Hotkeys>
		);
	}

	@autobind
	private addPanel(instance: string) {
		this.setState({activePanels: [...this.state.activePanels, instance]});
	}

	@autobind
	private removePanel(instance: string) {
		this.setState({activePanels: this.state.activePanels.filter((p) => p !== instance)});
	}

	public render() {
		const { barOpen, popupOpen, config } = this.state;

		return (
			<React.Fragment>
				<EditControls
					barOpen={barOpen}
					toggleBar={this.toggleBar}
					config={config}
					onConfigChange={this.onConfigChange}
					popupOpen={popupOpen}
					togglePopup={this.togglePopup}
				/>

				<SidebarContainer
					className={barOpen ? 'open' : undefined}
					leftSize={config.leftSize}
					rightSize={config.rightSize}
					highlightActive={config.highlightActive}
					alpha={config.alpha}
					barOpen={barOpen}
				>
					<div className={Classes.DARK + " sidebar-left edit-sidebar"} onClick={!barOpen ? this.toggleBar : undefined}>
						<Handel>
							<Icon icon={barOpen ? IconNames.DRAG_HANDLE_VERTICAL : IconNames.CHEVRON_RIGHT} iconSize={15} />
						</Handel>
						{barOpen && ( <EditMenu /> )}
					</div>

					<div className={Classes.DARK + " sidebar-right edit-sidebar"} onClick={!barOpen ? this.toggleBar : undefined}>
						<Handel>
							<Icon icon={barOpen ? IconNames.DRAG_HANDLE_VERTICAL : IconNames.CHEVRON_LEFT} iconSize={15} />
						</Handel>
						{barOpen && (
						<ScrollArea
							className="area edit-scroll"
							smoothScrolling={true}
							style={{ height: `calc(100vh - 10px)` }}
							speed={0.8}
								>
								<div className={Classes.DIALOG_BODY}>
									{barOpen && <ActiveEdit />}
								</div>
						</ScrollArea>
						)}
					</div>

					<div className="main-container">
						<PanelsActiveContext.Provider value={{addPanel: this.addPanel, removePanel: this.removePanel}}>
							{this.props.children}
						</PanelsActiveContext.Provider>
					</div>
				</SidebarContainer>


				{popupOpen && this.renderPopup()}
			</React.Fragment>
		);
	}

	private renderPopup() {
		return (
			<PopupWindow
				title="CW - Site Editor"
				windowProps={{
					width: 1024,
					height: 885
				}}
				autoClose
				onClose={() => this.setState({popupOpen: false})}
			>
				{<AdminPage config={this.state.config} onChange={this.onConfigChange} />}
			</PopupWindow>
		);
	}

	@autobind
	private toggleBar() {
		this.setState({barOpen: !this.state.barOpen});
	}

	@autobind
	private togglePopup() {
		this.setState({popupOpen: !this.state.popupOpen});
	}
}

const mapStateToProps = (state: typeof initialState) => {
	return {
		layout: state.layout,
		loading: !state.initialization.done,
		editingEnabled: state.edit.enabled
	};
};

export default connect(mapStateToProps)(EditorWrapper);







// WEBPACK FOOTER //
// ./src/core/components/editor/EditorWrapper.tsx